import React, { memo } from "react";
import { FaPhone, FaPlus, FaTrashAlt, FaUserFriends } from "react-icons/fa";
import { TextInput } from "../atoms";
import { CONTACTS_MAX, CONTACT_NAME_MAX, telHref } from "../../utils/firmContacts";

/**
 * Molekül: firma muhatapları (#367) — birden fazla "Muhatap + Telefon" satırı.
 * Firma Sahibi ve firma telefonu ayrı alanlardır; bu liste onlara ek.
 *
 * Firma oluşturma (İletişim adımı) ve firma detayı → Bilgi sekmesi ortak
 * kullanır. Satırlar form state'inde tutulur; boş satır kaydedilirken atılır
 * (utils/firmContacts.cleanContacts).
 *
 * @param {{name: string, tel: string}[]} value
 * @param {(rows) => void} onChange
 * @param {Object<number, {name?: string, tel?: string}>} [errors] satır hataları
 * @param {boolean} [readOnly] yalnız liste (düzenleme izni yok)
 * @param {boolean} [compact] Bilgi sekmesindeki kart içi küçük görünüm
 * @param {boolean} [isDirty] kaydedilmemiş değişiklik noktası
 */
function FirmContactsField({ value, onChange, errors = {}, readOnly = false, compact = false, isDirty = false }) {
  const rows = Array.isArray(value) ? value : [];

  const update = (index, field, next) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, [field]: next } : row)));
  const remove = (index) => onChange(rows.filter((_, i) => i !== index));
  const add = () => onChange([...rows, { name: "", tel: "" }]);

  const inputClass = compact ? "!px-3 !py-1.5 !rounded-lg text-sm !shadow-none" : "";

  return (
    <div
      className={`rounded-xl border p-3 sm:p-4 transition-all ${
        isDirty ? "border-blue-300 bg-blue-50/40" : "border-gray-200 bg-gray-50"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold text-gray-600 tracking-wide">
          <FaUserFriends className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500" />
          MUHATAPLAR
          {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 ml-1" />}
        </div>
        {!readOnly && rows.length < CONTACTS_MAX && (
          <button
            type="button"
            onClick={add}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
          >
            <FaPlus className="w-2.5 h-2.5" /> Muhatap Ekle
          </button>
        )}
      </div>

      {rows.length === 0 && (
        <p className="text-sm text-gray-400 italic">
          {readOnly ? "-" : "Muhatap eklenmedi"}
        </p>
      )}

      {readOnly ? (
        <ul className="space-y-1.5">
          {rows.map((row, i) => (
            <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
              <span className="font-semibold text-gray-800">{row.name || "-"}</span>
              {row.tel && (
                <a href={telHref(row.tel)} className="inline-flex items-center gap-1 text-emerald-700 hover:underline">
                  <FaPhone className="w-3 h-3" /> {row.tel}
                </a>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="flex items-start gap-2">
              <TextInput
                wrapperClassName="flex-1 min-w-0 !space-y-1"
                className={inputClass}
                placeholder="Muhatap adı"
                value={row.name}
                maxLength={CONTACT_NAME_MAX}
                onChange={(e) => update(i, "name", e.target.value)}
                error={!!errors[i]?.name}
                errorMessage={errors[i]?.name}
              />
              <TextInput
                wrapperClassName="flex-1 min-w-0 !space-y-1"
                className={inputClass}
                type="tel"
                placeholder="Telefon"
                value={row.tel}
                onChange={(e) => update(i, "tel", e.target.value)}
                error={!!errors[i]?.tel}
                errorMessage={errors[i]?.tel}
              />
              <button
                type="button"
                onClick={() => remove(i)}
                title="Muhatabı kaldır"
                className={`shrink-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors ${compact ? "" : "mt-1.5"}`}
              >
                <FaTrashAlt className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default memo(FirmContactsField);
